import { AIProviderComparisonScreen } from './routes/AIProviderComparisonScreen.js'
import { parseAIAnalyticsSearch } from './lib/ai-analytics.js'
import type { ApolloClient } from '@apollo/client'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'

import { AdminLayout } from './app/AdminLayout.js'
import { AdminNotFound } from './app/AdminNotFound.js'
import { AdminRouteError } from './app/AdminRouteError.js'
import { DEFAULT_LANGUAGE, isLanguage } from './lib/language.js'
import { parseAccountSearch, parseSubjectSearch } from './lib/list-search.js'
import { AccountScreen } from './routes/AccountScreen.js'
import { UpgradeRequestsScreen } from './routes/UpgradeRequestsScreen.js'
import { SubjectsScreen } from './routes/SubjectsScreen.js'
import { NewUserScreen } from './features/management/UserManagement.js'
import { NewSubjectScreen, SubjectScreen } from './features/management/SubjectManagement.js'
import { UsersScreen } from './routes/UsersScreen.js'

/**
 * Routes declared in code, for the same reason the product's are: the
 * generated route tree has to exist before `tsc` runs, and the router's
 * generator ships only as a Vite plugin. Generation that only happens inside
 * `vite dev` is generation CI does not do.
 */
export interface RouterContext {
  client: ApolloClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

/**
 * Every screen lives under `/$language`, exactly as in the product.
 *
 * The administration application is not an exception to the localization rule:
 * `/es/...` and `/en/...` are the canonical URLs and there is no unprefixed
 * one. A language-less URL redirects to Spanish rather than reading a stored
 * preference — the redirect happens before anything knows who is asking, and
 * Spanish is the answer the product gives when nothing is known.
 */
const languageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$language',
  beforeLoad: ({ location, params }) => {
    if (isLanguage(params.language)) return
    throw redirect({ href: `/${DEFAULT_LANGUAGE}${location.pathname}`, replace: true })
  },
  component: AdminLayout,
  // Inside the layout, localized, with a heading and a way back.
  notFoundComponent: AdminNotFound,
})

/**
 * The account list's search, filters, sort and page are the URL, so Back from
 * an account returns to the same narrowed page. See `lib/list-search.ts`.
 */
const usersRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/',
  component: UsersScreen,
  validateSearch: parseAccountSearch,
})

const accountRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/accounts/$userId',
  component: AccountScreen,
})

const requestsRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/requests',
  component: UpgradeRequestsScreen,
})

const auditRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/audit',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/$language', params, replace: true })
  },
})

/** A bare `/` lands on the default language rather than on nothing. */
const rootIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ href: `/${DEFAULT_LANGUAGE}`, replace: true })
  },
})

const newUserRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/users/new',
  component: NewUserScreen,
})
const subjectsRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/subjects',
  component: SubjectsScreen,
  validateSearch: parseSubjectSearch,
})
const newSubjectRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/subjects/new',
  component: NewSubjectScreen,
})
const subjectRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/subjects/$subjectId',
  component: SubjectScreen,
})

const aiRoute = createRoute({
  getParentRoute: () => languageRoute,
  path: '/ai',
  component: AIProviderComparisonScreen,
  validateSearch: parseAIAnalyticsSearch,
})

const routeTree = rootRoute.addChildren([
  rootIndexRoute,
  languageRoute.addChildren([
    aiRoute,
    usersRoute,
    accountRoute,
    requestsRoute,
    auditRoute,
    newUserRoute,
    subjectsRoute,
    newSubjectRoute,
    subjectRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  context: { client: undefined as never },
  // Localized, and without the error's own message: see `AdminRouteError`.
  defaultErrorComponent: AdminRouteError,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
